import { useCallback, useState } from "react";

export interface IManagedState<T> {
  states: T[];
  currentStateIndex: number;
}

export const getInitialState = <T,>(defaultState: T): IManagedState<T> => {
  return {
    states: [defaultState],
    currentStateIndex: 0,
  };
};

export const getCurrentState = <T,>(state: IManagedState<T>) => {
  return state.states[state.currentStateIndex];
};

export const setCurrentState = <T,>(
  state: IManagedState<T>,
  newState: T,
): IManagedState<T> => {
  return {
    ...state,
    states: [...state.states.slice(0, state.currentStateIndex + 1), newState],
    currentStateIndex: state.currentStateIndex + 1,
  };
};

export const setCurrentStateHidden = <T,>(
  state: IManagedState<T>,
  newState: T,
) => {
  return {
    ...state,
    states: [...state.states.slice(0, state.currentStateIndex), newState],
  };
};

export const resetManagedState = <T,>(
  state: IManagedState<T>,
): IManagedState<T> => {
  return {
    ...state,
    currentStateIndex: 0,
  };
};

export function useManagedState<T>(defaultState: T) {
  const [managedState, setManagedState] = useState<IManagedState<T>>(
    getInitialState(defaultState),
  );

  const setInitialState = useCallback((initialState: T) => {
    setManagedState(getInitialState(initialState));
  }, []);

  const setState = useCallback(
    (state: T | ((prevState: T) => T)) => {
      if (typeof state === "function") {
        setManagedState(
          setCurrentState(
            managedState,
            (state as (prevState: T) => T)(getCurrentState(managedState)),
          ),
        );
      } else {
        setManagedState(setCurrentState(managedState, state));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setStateHidden = useCallback(
    (state: T | ((prevState: T) => T)) => {
      if (typeof state === "function") {
        setManagedState(
          setCurrentStateHidden(
            managedState,
            (state as (prevState: T) => T)(getCurrentState(managedState)),
          ),
        );
      } else {
        setManagedState(setCurrentStateHidden(managedState, state));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const reset = useCallback(() => {
    setManagedState(resetManagedState(managedState));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    setInitialState,
    setState,
    setStateHidden,
    currentState: getCurrentState(managedState),
    reset,
  };
}
